import { coerce } from "gamla";
import { type JsonData, JsonEditor } from "json-edit-react";
import { type JsonSchema, jsonSchemaToZod } from "json-schema-to-zod";
import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { remoteConfigToolName } from "../../constants.ts";
import type { RemoteTool } from "../../instant.schema.ts";
import {
  BotNotFound,
  Button,
  Collapsible,
  inlineCodeClass,
  inputClass,
  labelClass,
  PageSpinner,
} from "./components.tsx";
import { transact, tx, useQuery } from "./db.ts";
import { SigningAuthDocs } from "./signingAuth.tsx";

const emptyTool = (): RemoteTool => ({
  name: "",
  description: "",
  url: "",
  parameters: { type: "object", properties: {}, required: [] },
});

export const CustomToolsConfig = ({ id }: { id: string }) => {
  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } } },
  });
  const [toolsDraft, setToolsDraft] = useState<RemoteTool[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isLoading && !error) {
      const [bot] = data.bots;
      if (bot?.tools) setToolsDraft(bot.tools);
    }
  }, [isLoading, error, data]);

  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  const [bot] = data.bots;
  if (!bot) return <BotNotFound />;

  const selectedTool = selectedIndex != null ? toolsDraft[selectedIndex] : null;

  const updateTool = (idx: number, patch: Partial<RemoteTool>) => {
    setToolsDraft((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...patch } as RemoteTool;
      return copy;
    });
  };

  const addTool = () => {
    setToolsDraft((prev) => [...prev, emptyTool()]);
    setSelectedIndex(toolsDraft.length);
  };

  const removeTool = (idx: number) => {
    setToolsDraft((prev) => prev.filter((_, i) => i !== idx));
    setSelectedIndex(null);
  };

  return (
    <Collapsible title="Custom Tools">
      <div className="flex flex-col gap-4">
        <div className="text-sm text-text-secondary-light dark:text-text-secondary">
          Define remote tools your bot can call. Each tool exposes an HTTP
          endpoint with JSON schema parameters following OpenAI tool format.
        </div>
        <div className="text-sm bg-accent/10 border border-accent/20 rounded p-3 text-accent-light dark:text-accent">
          <strong>Special Tool:</strong> If you create a tool named{" "}
          <code className="bg-accent/15 px-1 rounded">
            {remoteConfigToolName}
          </code>, it will be called on every incoming message to fetch dynamic
          conversation config. It won't be available as a callable tool for the
          agent. Return optional JSON fields like <code>prompt</code> and{" "}
          <code>timezoneIANA</code>. The signed request metadata already
          includes conversation context, so parameters can stay empty for this
          tool.
        </div>
        <SigningAuthDocs
          botId={bot.id}
          webhookSecret={bot.webhookSecret}
          extraNote={`// Note: For ${remoteConfigToolName} tool, params can be an empty object {} because conversation context is included in the signed request metadata.`}
        />
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="sm:w-1/3 w-full border border-border-default-light dark:border-border-default rounded p-2 flex flex-col gap-2 bg-surface-raised-light dark:bg-surface-raised">
            <div className="flex justify-between items-center mb-1">
              <div className="font-medium">Tools</div>
              <Button onClick={addTool} className="px-2 py-1 text-sm">
                + Add
              </Button>
            </div>
            {toolsDraft.length === 0 && (
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                No tools yet
              </div>
            )}
            <ul className="flex-1 overflow-y-auto flex flex-col gap-1">
              {toolsDraft.map((t, i) => {
                const active = selectedIndex === i;
                return (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex(i)}
                      className={`w-full text-left px-2 py-1 rounded border text-sm truncate transition ${
                        active
                          ? "bg-accent text-white border-accent"
                          : "bg-surface-overlay-light dark:bg-surface-overlay border-border-default-light dark:border-border-default hover:bg-surface-overlay-light dark:hover:bg-surface-overlay"
                      }`}
                      title={t.name || "(unnamed)"}
                    >
                      {t.name || (
                        <span className="italic text-text-tertiary-light dark:text-text-tertiary">
                          (unnamed)
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            {selectedIndex != null && (
              <Button
                onClick={() => removeTool(selectedIndex)}
                className="bg-danger hover:bg-danger-hover text-white mt-2"
              >
                Remove Selected
              </Button>
            )}
            <Button
              onClick={() => {
                toast.promise(
                  transact(tx.bots[id].update({ tools: toolsDraft })),
                  {
                    loading: "Saving...",
                    success: "Saved",
                    error: (e) => `Error: ${e.message}`,
                  },
                );
              }}
              disabled={toolsDraft.some((t) => !t.name || !t.url)}
              className="mt-2"
            >
              Save Tools
            </Button>
          </div>
          <div className="sm:w-2/3 w-full border border-border-default-light dark:border-border-default rounded p-4 flex flex-col gap-3 bg-surface-raised-light dark:bg-surface-raised min-h-[400px]">
            {selectedTool
              ? (
                <div className="flex flex-col gap-3">
                  <div>
                    <label className={labelClass}>
                      Name
                    </label>
                    <input
                      className={inputClass}
                      value={selectedTool.name}
                      onInput={(e) =>
                        updateTool(selectedIndex!, {
                          name: e.currentTarget.value,
                        })}
                      placeholder="summarize_doc"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Description
                    </label>
                    <textarea
                      className={inputClass}
                      rows={3}
                      value={selectedTool.description}
                      onInput={(e) =>
                        updateTool(selectedIndex!, {
                          description: e.currentTarget.value,
                        })}
                      placeholder="Summarize a document at a given URL"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      URL
                    </label>
                    <input
                      className={inputClass}
                      value={selectedTool.url}
                      onInput={(e) =>
                        updateTool(selectedIndex!, {
                          url: e.currentTarget.value,
                        })}
                      placeholder="https://example.com/tools/summarize"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      JSON Schema (parameters)
                    </label>
                    <div className="border border-border-default-light dark:border-border-default rounded overflow-hidden relative">
                      <JsonEditor
                        data={selectedTool.parameters}
                        setData={(parameters: JsonData) => {
                          updateTool(coerce(selectedIndex), {
                            parameters: parameters as JsonSchema,
                          });
                        }}
                      />
                    </div>
                    <div>
                      <div>Result:</div>
                      <code className={inlineCodeClass}>
                        {jsonSchemaToZod(selectedTool.parameters)}
                      </code>
                    </div>
                    <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
                      Must be a valid JSON schema for the tool parameters.
                    </div>
                  </div>
                </div>
              )
              : (
                <div className="text-sm text-text-tertiary-light dark:text-text-tertiary">
                  Select a tool to edit
                </div>
              )}
          </div>
        </div>
      </div>
    </Collapsible>
  );
};
